import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeftRight, Check, Search, Users } from "lucide-react";

const FIELDS = [["full_name", "Name"], ["email", "Email"], ["phone", "Phone"], ["dob", "Date of birth"], ["address", "Address"],
  ["citizenship", "Citizenship"], ["passport_no", "Passport no."], ["passport_expiry", "Passport expiry"], ["uci", "UCI"],
  ["status", "Status"], ["file_number", "File number"]];
const norm = (v) => (v ?? "").toString().trim();
const defaults = (keep, other) => Object.fromEntries(FIELDS.map(([k]) => [k, !norm(keep[k]) && norm(other[k]) ? "other" : "keep"]));

const Pick = ({ value, picked, differ, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`text-left px-3 py-2 rounded-lg border text-sm min-h-[38px] flex items-center justify-between gap-2 transition-colors ${picked ? "border-primary bg-primary/5 text-slate-900 font-semibold" : differ ? "border-amber-200 bg-amber-50 text-slate-500" : "border-slate-200 text-slate-400"}`}>
    <span className="truncate">{value || <span className="italic font-normal text-slate-300">blank</span>}</span>
    {picked && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
  </button>
);

// Client profile → find duplicate clients and merge one into this profile (the duplicate is deleted after everything moves over).
export function ClientMergeDialog({ clientId, onClose, onMerged }) {
  const [dups, setDups] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [pair, setPair] = useState(null);
  useEffect(() => { api.get(`/clients/${clientId}/duplicates`).then((r) => setDups(r.data.duplicates)).catch((e) => { toast.error(apiErr(e)); setDups([]); }); }, [clientId]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/clients?q=${encodeURIComponent(q.trim())}`).then((r) => setResults((r.data.items || r.data || []).filter((c) => c.id !== clientId).slice(0, 8))).catch(() => setResults([])), 300);
    return () => clearTimeout(t);
  }, [q, clientId]);
  const choose = async (otherId) => {
    try { const { data } = await api.get(`/clients/${clientId}/merge-candidate/${otherId}`); setPair(data); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="client-merge-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Merge duplicate clients</DialogTitle></DialogHeader>
        {pair ? <MergePicker pair={pair} onBack={() => setPair(null)} onMerged={onMerged} /> : (
          <div className="space-y-3">
            <div className="text-xs font-semibold text-slate-600">Possible duplicates (same email, phone or name)</div>
            {!dups ? <div className="text-xs text-slate-400">Checking…</div> : dups.length === 0 ? <div className="text-sm text-slate-500" data-testid="merge-no-dups">No obvious duplicates found. Search below to pick a client manually.</div> : (
              <div className="space-y-1.5">{dups.map((c) => <Candidate key={c.id} c={c} onPick={() => choose(c.id)} />)}</div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input data-testid="merge-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search another client by name, email or phone…" className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm" />
            </div>
            {results.length > 0 && <div className="space-y-1.5">{results.map((c) => <Candidate key={c.id} c={c} onPick={() => choose(c.id)} />)}</div>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Candidate({ c, onPick }) {
  return (
    <button data-testid={`merge-candidate-${c.id}`} onClick={onPick} className="w-full text-left rounded-xl border border-slate-200 px-3 py-2 hover:border-primary/50 hover:bg-primary/5 flex flex-wrap items-center gap-2 text-sm">
      <b className="text-slate-800">{c.full_name}</b>
      <span className="text-xs text-slate-500">{[c.email, c.phone, c.file_number && `File #${c.file_number}`].filter(Boolean).join(" · ")}</span>
      {c.match && <span className="ml-auto text-[10px] font-bold uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Same {c.match.join(" + ")}</span>}
    </button>
  );
}

function MergePicker({ pair, onBack, onMerged }) {
  const [swapped, setSwapped] = useState(false);
  const [keep, dup] = swapped ? [pair.other, pair.client] : [pair.client, pair.other];
  const [choices, setChoices] = useState(() => defaults(pair.client, pair.other));
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const swap = () => { const n = !swapped; setSwapped(n); setChoices(n ? defaults(pair.other, pair.client) : defaults(pair.client, pair.other)); setConfirming(false); };
  const finalName = choices.full_name === "other" ? dup.full_name : keep.full_name;
  const merge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${keep.id}/merge`, { other_id: dup.id, choices });
      const n = Object.values(data.moved || {}).reduce((a, b) => a + b, 0);
      toast.success(`Clients merged — ${n} linked record${n === 1 ? "" : "s"} moved, duplicate deleted`);
      onMerged(keep.id);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">Pick the value to keep for each field. Cases, payments, invoices, documents, notes and appointments all move to the kept client, then the duplicate is deleted (it stays in the deleted-items bin for 30 days).</p>
      <div className="grid grid-cols-[110px_1fr_1fr] gap-2 items-center text-xs">
        <span />
        <span className="font-bold text-emerald-700 uppercase tracking-wide truncate" data-testid="client-merge-keep-label">Kept · {keep.full_name}</span>
        <span className="font-bold text-rose-600 uppercase tracking-wide truncate" data-testid="client-merge-dup-label">Deleted · {dup.full_name}</span>
        {FIELDS.map(([k, label]) => {
          const differ = norm(keep[k]) !== norm(dup[k]);
          return [
            <span key={`${k}-l`} className="text-slate-500 font-medium">{label}</span>,
            <Pick key={`${k}-a`} testid={`client-merge-${k}-keep`} value={norm(keep[k])} picked={choices[k] === "keep"} differ={differ} onClick={() => setChoices({ ...choices, [k]: "keep" })} />,
            <Pick key={`${k}-b`} testid={`client-merge-${k}-other`} value={norm(dup[k])} picked={choices[k] === "other"} differ={differ} onClick={() => setChoices({ ...choices, [k]: "other" })} />,
          ];
        })}
      </div>
      {(keep.has_login || dup.has_login) && <p className="text-[11px] text-slate-400">{keep.has_login ? "The kept client's portal login stays." : "The duplicate's portal login moves to the kept client."}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <button type="button" onClick={onBack} className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600">Back</button>
          <button type="button" data-testid="client-merge-swap" onClick={swap} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600"><ArrowLeftRight className="h-4 w-4" /> Swap which stays</button>
        </div>
        {!confirming && <button type="button" data-testid="client-merge-continue" onClick={() => setConfirming(true)} className="bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm">Merge clients</button>}
      </div>
      {confirming && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 space-y-2" data-testid="client-merge-confirm-box">
          <div>Merge <b>{dup.full_name}</b> into <b>{finalName}</b>? Everything moves over and the duplicate is deleted.</div>
          <div className="flex gap-2">
            <button type="button" data-testid="client-merge-confirm" onClick={merge} disabled={busy} className="bg-rose-600 text-white px-4 py-2 rounded-xl font-semibold text-sm disabled:opacity-60">{busy ? "Merging…" : "Yes, merge"}</button>
            <button type="button" onClick={() => setConfirming(false)} className="px-4 py-2 rounded-xl border border-rose-200 text-sm font-semibold">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
